import { FIELD_TO_VIEW, fieldsToView, getFormattedPrice } from 'utils/utils';
import { format } from 'date-fns';
import { action, makeObservable, observable, runInAction } from 'mobx';
import Cookies from 'universal-cookie';
import { DATA_SECTION } from 'routes';

const CalendarAPIOptions = {
	client_id: process.env.GOOGLE_CAL_CLIENT_ID,
	apiKey: process.env.GOOGLE_CAL_API_KEY,
	discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
	scope: 'https://www.googleapis.com/auth/calendar',
};

export class CalendarAPI {
	COOKIE_ACCESS_TOKEN = 'acc_tok';

	// google services
	gapiInited = false;
	gisInited = false;

	// google tokens
	tokenClient = null;
	cookies = new Cookies();

	// data
	events = null;
	calendars = null;

	// states
	isLoading = false;
	isExporing = false;

	constructor() {
		// makeAutoObservable(this);

		makeObservable(this, {
			events: observable,
			calendars: observable,

			gapiInited: observable,
			gisInited: observable,

			isLoading: observable,
			getCals: action,

			isExporing: observable,
			exportEvent: action,
		});

		this.init();
	}

	/**
	 * Init Calendar API
	 */

	init = () => {
		console.log('INIT CALENDAR');

		// append API and client
		this.appendScript('https://apis.google.com/js/api.js', this.gapiLoaded);
		this.appendScript('https://accounts.google.com/gsi/client', this.gisLoaded);
	};

	/**
	 * Append a script
	 * @param url
	 * @param callback
	 */

	appendScript = (url: string, callback: () => void) => {
		const script = document.createElement('script');
		script.src = url;
		script.async = true;
		script.onload = () => {
			console.log(url, 'ready!');

			if (callback) {
				callback();
			}
		};

		document.body.appendChild(script);
	};

	/**
	 * Google API Loaded Callback
	 */

	gapiLoaded = () => {
		gapi.load('client', this.initGapiClient);
	};

	initGapiClient = async () => {
		console.log('INIT GAPI CLIENT');

		await gapi.client
			.init({
				apiKey: CalendarAPIOptions.apiKey,
				discoveryDocs: CalendarAPIOptions.discoveryDocs,
			})
			.then(() => {
				console.log('GAPI CLIENT INIT');
				const accessToken = this.getAccessToken();

				if (this.getAccessToken()) {
					gapi.auth.setToken({ access_token: accessToken });
				}
			});

		this.gapiInited = true;
	};

	/**
	 * Google Identity Service Loaded
	 */

	gisLoaded = () => {
		this.tokenClient = google.accounts.oauth2.initTokenClient({
			client_id: CalendarAPIOptions.client_id,
			scope: CalendarAPIOptions.scope,
			callback: '',
		});

		this.gisInited = true;
	};

	/**
	 * Authorization handle
	 */

	handleClickAuth = () => {
		this.tokenClient.callback = async (resp) => {
			if (resp.error !== undefined) {
				console.log(resp);
			}

			this.storeAccessToken(resp.access_token);
		};

		if (gapi.client.getToken() === null) {
			this.tokenClient.requestAccessToken({ prompt: 'consent' });
		} else {
			this.tokenClient.requestAccessToken({ prompt: '' });
		}
	};

	/**
	 * Stores access token
	 * @param accessToken
	 */

	storeAccessToken = (accessToken) => {
		console.log(this.COOKIE_ACCESS_TOKEN, accessToken);
		this.cookies.set(this.COOKIE_ACCESS_TOKEN, accessToken);
	};

	/**
	 * Get Access Token
	 * @returns
	 */

	getAccessToken = () => {
		return this.cookies.get(this.COOKIE_ACCESS_TOKEN);
	};

	/**
	 * Get Upcomming Events
	 */

	getCals = async () => {
		let response;

		try {
			runInAction(() => {
				this.isLoading = true;
			});

			response = await gapi.client.calendar.calendarList.list();

			this.calendars = response.result.items;
		} catch (err) {
			console.log('Calendar listing', err);
		} finally {
			runInAction(() => {
				this.isLoading = false;
			});
		}
	};

	/**
	 * Exports an event
	 */

	exportEvent = async (calendarId, eventData, exportPrices, onSuccess, onError) => {
		let response;
		console.log('exporting');

		const currentTime = new Date();
		const startTime = currentTime.toISOString();

		// Calculate the end time (e.g., 1 hour later)
		const endTime = new Date(currentTime.getTime() + 60 * 60 * 1000).toISOString();

		const description = this.transformData(eventData, exportPrices);

		// if is not confirmed or event_name is not set, put here the city
		let summaryTitle = eventData?.artist?.name + ' - ';

		if (eventData.confirmed) {
			summaryTitle += eventData.event_name + ' (' + eventData.event_address_city + ')';
		} else {
			summaryTitle += eventData.event_address_city;
		}

		// return;

		const times = this.getEventTimes(eventData?.event_date, eventData?.time_performance);

		const event = {
			summary: summaryTitle,
			description: description,
			start: {
				dateTime: times.start,
				timeZone: 'Europe/Prague',
			},
			end: {
				dateTime: times.end,
				timeZone: 'Europe/Prague',
			},
		};

		try {
			runInAction(() => {
				this.isExporing = true;
			});

			response = await gapi.client.calendar.events.insert({
				calendarId: calendarId ? calendarId : 'primary', // Use 'primary' for the user's primary calendar
				resource: event,
			});

			console.log('export', response);
			onSuccess();
		} catch (err) {
			console.log('Export error', err);
			onError(err);
		} finally {
			runInAction(() => {
				this.isExporing = false;
			});
		}
	};

	getEventTimes = (date, time) => {
		console.log('DATE', date);
		console.log('TIME', time);

		const startTime = time ? time : '00:00:00';

		const times = {
			start: date + 'T' + startTime,
			end: date + 'T' + startTime,
		};

		return times;
	};

	/**
	 * Transform event data for event summary
	 * @param data
	 * @returns
	 */

	transformData = (data, exportPrices) => {
		let output = '';

		for (const sectionId in fieldsToView) {
			// console.log('SECTION', fieldsToView[sectionId]);

			if (!exportPrices && fieldsToView[sectionId].id === DATA_SECTION.FEE) {
				console.log('HONORAR NOT EXPORT');

				continue;
			}

			output += '<h3>' + fieldsToView[sectionId].title + '</h3>';

			for (const field in fieldsToView[sectionId].fields) {
				const fieldDetail = fieldsToView[sectionId].fields[field];

				console.log('SECTION FIELD', fieldDetail);

				if (fieldDetail.dependsOn) {
					console.log(field, 'depends on------');

					const propertyValueDependsOn = data[fieldDetail.dependsOn.property];

					console.log(
						'-------->>>>>>> ' + propertyValueDependsOn + ' VS' + fieldDetail.dependsOn.value,
					);

					if (propertyValueDependsOn !== fieldDetail.dependsOn.value) {
						continue;
					}
				}

				output += this.getSummaryLine(fieldDetail, data[fieldDetail.key], data);
			}

			output += '<hr />';
		}

		return output;
	};

	// NOTE: data parametr obsahuje vsechny data, abychom si mohli sahat na VAT apod.

	getSummaryLine = (field, value, data) => {
		let val = value ? value : '<em>Bude doplněno.</em>';

		if (
			value ||
			field.type === FIELD_TO_VIEW.BOOLEAN ||
			(field.type === FIELD_TO_VIEW.DISTANCE && value !== null) ||
			field.type === FIELD_TO_VIEW.PRICE
		) {
			// TODO false issue here, kdyz je boolean, udelat podminky jako jinde.
			switch (field.type) {
				// TOOD: blbne false value
				case FIELD_TO_VIEW.BOOLEAN:
					console.log('BOOL VALUE', value);

					val = value ? 'Ano' : 'Ne';
					break;

				case FIELD_TO_VIEW.DATE:
					val = format(new Date(val), 'dd. MM. YYY');
					break;

				case FIELD_TO_VIEW.DISTANCE:
					val = value + ' km';
					break;

				case FIELD_TO_VIEW.ENUM:
					val = field.enumOptions[value];
					break;

				case FIELD_TO_VIEW.PRICE:
					val = getFormattedPrice(value, data.fee_vat);

					break;

				case FIELD_TO_VIEW.TIME:
					val = format(new Date('2020-01-01 ' + value), 'HH:mm');
					break;
			}
		}

		return '<strong>' + field.name + ':</strong> ' + val + '\r\n';
	};
}
